// ═══════════════════════════════════════════════════════════════════════════
// Permission Constants
// Organized by module for easier module-permission mapping
// ═══════════════════════════════════════════════════════════════════════════

export const PERMISSIONS = {
  // ───────────────────────────────────────────────────────────────────────────
  // User Management (WEB_APP module)
  // ───────────────────────────────────────────────────────────────────────────
  USERS_VIEW: "users:view",
  USERS_CREATE: "users:create",
  USERS_UPDATE: "users:update",
  USERS_DELETE: "users:delete",
  USERS_MANAGE_ROLES: "users:manage_roles", // NEW: Assign/revoke roles

  // ───────────────────────────────────────────────────────────────────────────
  // Product Management (INVENTORY module)
  // ───────────────────────────────────────────────────────────────────────────
  PRODUCTS_VIEW: "products:view",
  PRODUCTS_CREATE: "products:create",
  PRODUCTS_UPDATE: "products:update",
  PRODUCTS_DELETE: "products:delete",
  PRODUCTS_IMPORT: "products:import", // NEW: Bulk import
  PRODUCTS_EXPORT: "products:export", // NEW: Export to CSV/Excel

  // ───────────────────────────────────────────────────────────────────────────
  // Inventory Management (INVENTORY module)
  // ───────────────────────────────────────────────────────────────────────────
  INVENTORY_VIEW: "inventory:view",
  INVENTORY_UPDATE: "inventory:update",
  INVENTORY_ADJUST: "inventory:adjust",
  INVENTORY_TRANSFER: "inventory:transfer", // NEW: Transfer between stores
  INVENTORY_COUNT: "inventory:count",

  // Versioned catalog and opening-stock imports
  IMPORTS_MANAGE: "imports:manage",
  IMPORTS_APPROVE: "imports:approve",

  // ───────────────────────────────────────────────────────────────────────────
  // Billing (POS_BILLING module)
  // ───────────────────────────────────────────────────────────────────────────
  BILLING_CREATE: "billing:create",
  BILLING_VIEW: "billing:view",
  BILLING_UPDATE: "billing:update",
  BILLING_DELETE: "billing:delete",
  BILLING_REFUND: "billing:refund", // NEW: Process refunds
  BILLING_DISCOUNT: "billing:discount", // NEW: Apply discounts
  BILLING_VOID: "billing:void", // NEW: Void invoices
  SALES_QUOTE: "sales:quote",
  SALES_ACCEPT: "sales:accept",
  SALES_VOID: "sales:void",
  SALES_DISCOUNT_OVERRIDE: "sales:discount_override",
  RETURNS_CREATE: "returns:create",
  RETURNS_REFUND: "returns:refund",
  COUNTER_OPERATE: "counter:operate",
  DOCUMENTS_REPRINT: "documents:reprint",
  DOCUMENTS_BACKDATE: "documents:backdate",

  // ───────────────────────────────────────────────────────────────────────────
  // Customer Management (CUSTOMER_MGMT module)
  // ───────────────────────────────────────────────────────────────────────────
  CUSTOMERS_VIEW: "customers:view",
  CUSTOMERS_CREATE: "customers:create",
  CUSTOMERS_UPDATE: "customers:update",
  CUSTOMERS_DELETE: "customers:delete",
  CUSTOMERS_EXPORT: "customers:export", // NEW: Export customer data

  // ───────────────────────────────────────────────────────────────────────────
  // Reports (REPORTS_BASIC / REPORTS_ADVANCED module)
  // ───────────────────────────────────────────────────────────────────────────
  REPORTS_VIEW: "reports:view",
  REPORTS_EXPORT: "reports:export",
  REPORTS_ADVANCED: "reports:advanced", // NEW: Access advanced analytics

  // Accounting period controls
  ACCOUNTING_PERIOD_VIEW: "accounting:period_view",
  ACCOUNTING_PERIOD_CLOSE: "accounting:period_close",
  ACCOUNTING_PERIOD_REOPEN: "accounting:period_reopen",
  ACCOUNTING_LATE_POST: "accounting:late_post",

  // ───────────────────────────────────────────────────────────────────────────
  // Settings (WEB_APP module)
  // ───────────────────────────────────────────────────────────────────────────
  SETTINGS_VIEW: "settings:view",
  SETTINGS_UPDATE: "settings:update",
  SETTINGS_BILLING: "settings:billing", // NEW: Manage billing/subscription
  SUBSCRIPTION_MANAGE: "subscription:manage",

  // ───────────────────────────────────────────────────────────────────────────
  // Compliance (COMPLIANCE module - pharmacy specific)
  // ───────────────────────────────────────────────────────────────────────────
  COMPLIANCE_VIEW: "compliance:view",
  COMPLIANCE_MANAGE: "compliance:manage",
  PRESCRIPTIONS_VIEW: "prescriptions:view",
  PRESCRIPTIONS_MANAGE: "prescriptions:manage",
  AUDIT_VIEW: "audit:view",

  // Canonical master-data lifecycle
  MASTER_DATA_VIEW: "master_data:view",
  MASTER_DATA_MANAGE: "master_data:manage",
  MASTER_DATA_APPROVE: "master_data:approve",

  // Evidence-backed maker-checker workflow
  APPROVALS_REQUEST: "approvals:request",
  APPROVALS_REVIEW: "approvals:review",
  GST_OVERRIDE: "gst:override",
  SENSITIVE_PATIENT_VIEW: "sensitive:patient:view",
  SENSITIVE_PRESCRIPTION_VIEW: "sensitive:prescription:view",
  SENSITIVE_GST_VIEW: "sensitive:gst:view",
  SENSITIVE_BANK_VIEW: "sensitive:bank:view",
  SENSITIVE_PAYMENT_VIEW: "sensitive:payment:view",

  // ───────────────────────────────────────────────────────────────────────────
  // Store Management (MULTI_STORE module)
  // ───────────────────────────────────────────────────────────────────────────
  STORES_VIEW: "stores:view",
  STORES_CREATE: "stores:create",
  STORES_UPDATE: "stores:update",
  STORES_DELETE: "stores:delete",

  // ───────────────────────────────────────────────────────────────────────────
  // Supplier Management (SUPPLIER_MGMT module)
  // ───────────────────────────────────────────────────────────────────────────
  SUPPLIERS_VIEW: "suppliers:view",
  SUPPLIERS_CREATE: "suppliers:create",
  SUPPLIERS_UPDATE: "suppliers:update",
  SUPPLIERS_DELETE: "suppliers:delete",

  // ───────────────────────────────────────────────────────────────────────────
  // Purchase Orders (INVENTORY module)
  // ───────────────────────────────────────────────────────────────────────────
  PURCHASE_ORDERS_VIEW: "purchase_orders:view",
  PURCHASE_ORDERS_CREATE: "purchase_orders:create",
  PURCHASE_ORDERS_APPROVE: "purchase_orders:approve",
  PURCHASE_ORDERS_RECEIVE: "purchase_orders:receive",

  // ───────────────────────────────────────────────────────────────────────────
  // Payments (PAYMENTS module)
  // ───────────────────────────────────────────────────────────────────────────
  PAYMENTS_VIEW: "payments:view",
  PAYMENTS_CREATE: "payments:create",
  PAYMENTS_ADJUST: "payments:adjust",
  PAYMENTS_REFUND: "payments:refund",
  PAYMENTS_SETTLE: "payments:settle",
  PAYMENTS_DELETE: "payments:delete",

  // ───────────────────────────────────────────────────────────────────────────
  // Procurement (PROCUREMENT module)
  // ───────────────────────────────────────────────────────────────────────────
  PROCUREMENT_VIEW: "procurement:view",
  PROCUREMENT_CREATE: "procurement:create",
  PROCUREMENT_APPROVE: "procurement:approve",
  SUPPLIER_PAYMENT_CREATE: "supplier_payment:create",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ═══════════════════════════════════════════════════════════════════════════
// Role Hierarchy
// Lower number = higher privilege level (1 = highest)
// ═══════════════════════════════════════════════════════════════════════════

export const ROLE_HIERARCHY: Record<string, number> = {
  SUPER_ADMIN: 1,
  BUSINESS_OWNER: 10,
  STORE_MANAGER: 20,
  PHARMACIST: 30,
  CASHIER: 50,
  BILLING_STAFF: 40,
  INVENTORY_STAFF: 40,
  VIEWER: 50,
};

// ═══════════════════════════════════════════════════════════════════════════
// Role Permission Mapping
// Each role has explicit permissions assigned
// ═══════════════════════════════════════════════════════════════════════════

export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  SUPER_ADMIN: Object.values(PERMISSIONS),

  BUSINESS_OWNER: [
    // User management
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_UPDATE,
    PERMISSIONS.USERS_MANAGE_ROLES,
    // Products
    PERMISSIONS.PRODUCTS_VIEW,
    PERMISSIONS.PRODUCTS_CREATE,
    PERMISSIONS.PRODUCTS_UPDATE,
    PERMISSIONS.PRODUCTS_IMPORT,
    PERMISSIONS.PRODUCTS_EXPORT,
    // Inventory
    PERMISSIONS.INVENTORY_VIEW,
    PERMISSIONS.INVENTORY_UPDATE,
    PERMISSIONS.INVENTORY_TRANSFER,
    // Billing
    PERMISSIONS.BILLING_VIEW,
    PERMISSIONS.BILLING_REFUND,
    PERMISSIONS.BILLING_VOID,
    // Customers
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_CREATE,
    PERMISSIONS.CUSTOMERS_UPDATE,
    PERMISSIONS.CUSTOMERS_EXPORT,
    // Reports
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.REPORTS_ADVANCED,
    PERMISSIONS.ACCOUNTING_PERIOD_VIEW,
    PERMISSIONS.ACCOUNTING_PERIOD_CLOSE,
    PERMISSIONS.ACCOUNTING_PERIOD_REOPEN,
    PERMISSIONS.ACCOUNTING_LATE_POST,
    // Settings
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.SETTINGS_UPDATE,
    PERMISSIONS.SETTINGS_BILLING,
    PERMISSIONS.SUBSCRIPTION_MANAGE,
    // Compliance
    PERMISSIONS.COMPLIANCE_VIEW,
    PERMISSIONS.MASTER_DATA_VIEW,
    PERMISSIONS.MASTER_DATA_MANAGE,
    PERMISSIONS.MASTER_DATA_APPROVE,
    // Stores
    PERMISSIONS.STORES_VIEW,
    PERMISSIONS.STORES_CREATE,
    PERMISSIONS.STORES_UPDATE,
    // Suppliers
    PERMISSIONS.SUPPLIERS_VIEW,
    PERMISSIONS.SUPPLIERS_CREATE,
    PERMISSIONS.SUPPLIERS_UPDATE,
    // Purchase Orders
    PERMISSIONS.PURCHASE_ORDERS_VIEW,
    PERMISSIONS.PURCHASE_ORDERS_APPROVE,
    // Payments
    PERMISSIONS.PAYMENTS_VIEW,
    PERMISSIONS.PAYMENTS_CREATE,
    PERMISSIONS.PAYMENTS_ADJUST,
    PERMISSIONS.PAYMENTS_REFUND,
    PERMISSIONS.PAYMENTS_SETTLE,
    PERMISSIONS.PAYMENTS_DELETE,
    // Procurement
    PERMISSIONS.PROCUREMENT_VIEW,
    PERMISSIONS.PROCUREMENT_CREATE,
  ],

  STORE_MANAGER: [
    // Products
    PERMISSIONS.PRODUCTS_VIEW,
    PERMISSIONS.PRODUCTS_CREATE,
    PERMISSIONS.PRODUCTS_UPDATE,
    PERMISSIONS.PRODUCTS_IMPORT,
    // Inventory
    PERMISSIONS.INVENTORY_VIEW,
    PERMISSIONS.INVENTORY_UPDATE,
    // Billing
    PERMISSIONS.BILLING_VIEW,
    PERMISSIONS.BILLING_CREATE,
    PERMISSIONS.BILLING_REFUND,
    PERMISSIONS.BILLING_DISCOUNT,
    // Customers
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_CREATE,
    // Reports
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.ACCOUNTING_PERIOD_VIEW,
    // Compliance
    PERMISSIONS.COMPLIANCE_VIEW,
    PERMISSIONS.MASTER_DATA_VIEW,
    PERMISSIONS.MASTER_DATA_MANAGE,
    PERMISSIONS.MASTER_DATA_APPROVE,
    // Suppliers
    PERMISSIONS.SUPPLIERS_VIEW,
    // Purchase Orders
    PERMISSIONS.PURCHASE_ORDERS_VIEW,
    PERMISSIONS.PURCHASE_ORDERS_CREATE,
    PERMISSIONS.PURCHASE_ORDERS_RECEIVE,
    // Procurement
    PERMISSIONS.PROCUREMENT_VIEW,
    PERMISSIONS.PROCUREMENT_CREATE,
    // Payments
    PERMISSIONS.PAYMENTS_VIEW,
    PERMISSIONS.PAYMENTS_ADJUST,
    PERMISSIONS.PAYMENTS_REFUND,
    PERMISSIONS.PAYMENTS_SETTLE,
  ],

  PHARMACIST: [
    // Products
    PERMISSIONS.PRODUCTS_VIEW,
    // Inventory
    PERMISSIONS.INVENTORY_VIEW,
    // Billing
    PERMISSIONS.BILLING_CREATE,
    PERMISSIONS.BILLING_VIEW,
    PERMISSIONS.BILLING_DISCOUNT,
    // Customers
    PERMISSIONS.CUSTOMERS_VIEW,
    // Compliance
    PERMISSIONS.COMPLIANCE_VIEW,
    PERMISSIONS.COMPLIANCE_MANAGE,
    PERMISSIONS.MASTER_DATA_VIEW,
  ],

  BILLING_STAFF: [
    // Products
    PERMISSIONS.PRODUCTS_VIEW,
    PERMISSIONS.MASTER_DATA_VIEW,
    // Billing
    PERMISSIONS.BILLING_CREATE,
    PERMISSIONS.BILLING_VIEW,
    // Customers
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_CREATE,
  ],

  INVENTORY_STAFF: [
    // Products
    PERMISSIONS.PRODUCTS_VIEW,
    PERMISSIONS.PRODUCTS_CREATE,
    PERMISSIONS.PRODUCTS_UPDATE,
    PERMISSIONS.PRODUCTS_IMPORT,
    PERMISSIONS.MASTER_DATA_VIEW,
    PERMISSIONS.MASTER_DATA_MANAGE,
    // Inventory
    PERMISSIONS.INVENTORY_VIEW,
    PERMISSIONS.INVENTORY_UPDATE,
    PERMISSIONS.INVENTORY_ADJUST,
    // Suppliers
    PERMISSIONS.SUPPLIERS_VIEW,
    // Purchase Orders
    PERMISSIONS.PURCHASE_ORDERS_VIEW,
    PERMISSIONS.PURCHASE_ORDERS_RECEIVE,
  ],

  VIEWER: [
    // Read-only access
    PERMISSIONS.PRODUCTS_VIEW,
    PERMISSIONS.INVENTORY_VIEW,
    PERMISSIONS.BILLING_VIEW,
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.SUPPLIERS_VIEW,
    PERMISSIONS.PURCHASE_ORDERS_VIEW,
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// Module to Permission Mapping
// Used to auto-gate permissions based on module access
// ═══════════════════════════════════════════════════════════════════════════

export const MODULE_PERMISSIONS: Record<string, Permission[]> = {
  INVENTORY: [
    PERMISSIONS.PRODUCTS_VIEW,
    PERMISSIONS.PRODUCTS_CREATE,
    PERMISSIONS.PRODUCTS_UPDATE,
    PERMISSIONS.PRODUCTS_DELETE,
    PERMISSIONS.PRODUCTS_IMPORT,
    PERMISSIONS.PRODUCTS_EXPORT,
    PERMISSIONS.INVENTORY_VIEW,
    PERMISSIONS.INVENTORY_UPDATE,
    PERMISSIONS.INVENTORY_ADJUST,
    PERMISSIONS.INVENTORY_TRANSFER,
    PERMISSIONS.INVENTORY_COUNT,
  ],
  CUSTOMER_MGMT: [
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_CREATE,
    PERMISSIONS.CUSTOMERS_UPDATE,
    PERMISSIONS.CUSTOMERS_DELETE,
    PERMISSIONS.CUSTOMERS_EXPORT,
  ],
  REPORTS: [
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.REPORTS_ADVANCED,
    PERMISSIONS.ACCOUNTING_PERIOD_VIEW,
    PERMISSIONS.ACCOUNTING_PERIOD_CLOSE,
    PERMISSIONS.ACCOUNTING_PERIOD_REOPEN,
    PERMISSIONS.ACCOUNTING_LATE_POST,
  ],
  ACCOUNT_BILLING: [PERMISSIONS.SUBSCRIPTION_MANAGE],
  MULTI_STORE: [
    PERMISSIONS.STORES_VIEW,
    PERMISSIONS.STORES_CREATE,
    PERMISSIONS.STORES_UPDATE,
    PERMISSIONS.STORES_DELETE,
    PERMISSIONS.INVENTORY_TRANSFER,
  ],
  SUPPLIER_MGMT: [
    PERMISSIONS.SUPPLIERS_VIEW,
    PERMISSIONS.SUPPLIERS_CREATE,
    PERMISSIONS.SUPPLIERS_UPDATE,
    PERMISSIONS.SUPPLIERS_DELETE,
  ],
  PROCUREMENT: [
    PERMISSIONS.PROCUREMENT_VIEW,
    PERMISSIONS.PROCUREMENT_CREATE,
    PERMISSIONS.PROCUREMENT_APPROVE,
    PERMISSIONS.SUPPLIER_PAYMENT_CREATE,
    PERMISSIONS.PURCHASE_ORDERS_VIEW,
    PERMISSIONS.PURCHASE_ORDERS_CREATE,
    PERMISSIONS.PURCHASE_ORDERS_APPROVE,
    PERMISSIONS.PURCHASE_ORDERS_RECEIVE,
  ],
  SALES_MGMT: [
    PERMISSIONS.BILLING_CREATE,
    PERMISSIONS.BILLING_VIEW,
    PERMISSIONS.BILLING_UPDATE,
    PERMISSIONS.BILLING_DELETE,
    PERMISSIONS.BILLING_REFUND,
    PERMISSIONS.BILLING_DISCOUNT,
    PERMISSIONS.BILLING_VOID,
    PERMISSIONS.PAYMENTS_VIEW,
    PERMISSIONS.PAYMENTS_CREATE,
    PERMISSIONS.PAYMENTS_ADJUST,
    PERMISSIONS.PAYMENTS_REFUND,
    PERMISSIONS.PAYMENTS_SETTLE,
    PERMISSIONS.PAYMENTS_DELETE,
  ],
};
